import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BuildingControlProjectsType } from "../../Types/BuildingControl/BuildingControlProjectsType";
import { fetchAllBuildingControlProjects, fetchBuildingControlQuotationRequests, fetchBuildingControlQuotations, fetchBuildingInspectionCompanies } from "../../Thunks/BuildingControlThunks";
import {
    BuildingControlQuotation,
    BuildingControlQuotationRequest,
    BuildingInspectionCompany
} from "../../Types/BuildingControl/BuildingControlQuotationType";

interface BuildingControlSlice {
    loadingBuildingControlProjects: boolean;
    buildingControlProjects: BuildingControlProjectsType[];
    errorBuildingControlProjects: string | null;

    loadingBuildingInspectionCompanies: boolean;
    buildingInspectionCompanies: BuildingInspectionCompany[];
    errorBuildingInspectionCompanies: string | null;
    isFetchedBuildingInspectionCompanies: boolean;

    loadingBuildingControlQuotations: boolean;
    buildingControlQuotations: BuildingControlQuotation[];
    errorBuildingControlQuotations: string | null;
    isFetchedBuildingControlQuotations: boolean;

    loadingBuildingControlQuotationRequests: boolean;
    buildingControlQuotationRequests: BuildingControlQuotationRequest[];
    errorBuildingControlQuotationRequests: string | null;
    isFetchedBuildingControlQuotationRequests: boolean;
}

// set initial values
const initialState: BuildingControlSlice = {
    loadingBuildingControlProjects: false,
    buildingControlProjects: [],
    errorBuildingControlProjects: null,

    loadingBuildingInspectionCompanies: false,
    buildingInspectionCompanies: [],
    errorBuildingInspectionCompanies: null,
    isFetchedBuildingInspectionCompanies: false,

    loadingBuildingControlQuotations: false,
    buildingControlQuotations: [],
    errorBuildingControlQuotations: null,
    isFetchedBuildingControlQuotations: false,

    loadingBuildingControlQuotationRequests: false,
    buildingControlQuotationRequests: [],
    errorBuildingControlQuotationRequests: null,
    isFetchedBuildingControlQuotationRequests: false,
};

// set state value for api response
const buildingControlSlice = createSlice({
    name: "buildingControl",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Builing control projects
            .addCase(fetchAllBuildingControlProjects.pending, (state) => {
                state.loadingBuildingControlProjects = true;
                state.errorBuildingControlProjects = null;
            })
            .addCase(fetchAllBuildingControlProjects.fulfilled, (state, action: PayloadAction<BuildingControlProjectsType[]>) => {
                state.loadingBuildingControlProjects = false;
                state.buildingControlProjects = action.payload;
            })
            .addCase(fetchAllBuildingControlProjects.rejected, (state) => {
                state.loadingBuildingControlProjects = false;
                state.errorBuildingControlProjects = "Failed to fetch building control projects";
            })

            // Building inspection companies
            .addCase(fetchBuildingInspectionCompanies.pending, (state) => {
                state.loadingBuildingInspectionCompanies = true;
                state.errorBuildingInspectionCompanies = null;
            })
            .addCase(fetchBuildingInspectionCompanies.fulfilled, (state, action: PayloadAction<BuildingInspectionCompany[]>) => {
                state.loadingBuildingInspectionCompanies = false;

                const activeData = action?.payload?.filter((item) => item?.status === 1);
                state.buildingInspectionCompanies = activeData;

                state.isFetchedBuildingInspectionCompanies = true;
            })
            .addCase(fetchBuildingInspectionCompanies.rejected, (state) => {
                state.loadingBuildingInspectionCompanies = false;
                state.errorBuildingInspectionCompanies = "Failed to fetch building inspection companies";
                state.isFetchedBuildingInspectionCompanies = true;
            })

            // Building control quotations
            .addCase(fetchBuildingControlQuotations.pending, (state) => {
                state.loadingBuildingControlQuotations = true;
                state.errorBuildingControlQuotations = null;
            })
            .addCase(fetchBuildingControlQuotations.fulfilled, (state, action: PayloadAction<BuildingControlQuotation[]>) => {
                state.loadingBuildingControlQuotations = false;
                state.buildingControlQuotations = action.payload;
                state.isFetchedBuildingControlQuotations = true;
            })
            .addCase(fetchBuildingControlQuotations.rejected, (state) => {
                state.loadingBuildingControlQuotations = false;
                state.errorBuildingControlQuotations = "Failed to fetch building control quotations";
                state.isFetchedBuildingControlQuotations = true;
            })

            // Building control quotation requests
            .addCase(fetchBuildingControlQuotationRequests.pending, (state) => {
                state.loadingBuildingControlQuotationRequests = true;
                state.errorBuildingControlQuotationRequests = null;
            })
            .addCase(fetchBuildingControlQuotationRequests.fulfilled, (state, action: PayloadAction<BuildingControlQuotationRequest[]>) => {
                state.loadingBuildingControlQuotationRequests = false;
                state.buildingControlQuotationRequests = action.payload;
                state.isFetchedBuildingControlQuotationRequests = true;
            })
            .addCase(fetchBuildingControlQuotationRequests.rejected, (state) => {
                state.loadingBuildingControlQuotationRequests = false;
                state.errorBuildingControlQuotationRequests = "Failed to fetch building control quotation requests";
                state.isFetchedBuildingControlQuotationRequests = true;
            });
    },
});

export default buildingControlSlice.reducer;
